/**
 * RemoteFile — transcript 里的远端文件（用户上传的附件 / 桌面回传的产物）。
 *
 * 挂载时拉预签名 GET；图片显示缩略，其它文件点开下载（点击重拉新鲜 URL，
 * 预签名 ~10min 会过期）。从 Session.tsx 抽出来独立成组件。
 */

import { useEffect, useState } from 'react'

import { getUpload, type UploadInfo } from '../lib/api'
import { useLang } from '../contexts/LanguageContext'
import { Spinner } from './ui'
import { IconFile, IconDownload } from './icons'

export function fmtSize(bytes?: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export default function RemoteFile({ uploadId, fallbackName, fallbackKind, size, fromDesktop }: {
  uploadId: string
  fallbackName?: string
  fallbackKind: 'image' | 'file'
  size?: number
  fromDesktop?: boolean
}) {
  const { t } = useLang()
  const [info, setInfo] = useState<UploadInfo | null>(null)
  const [failed, setFailed] = useState(false)
  const [opening, setOpening] = useState(false)

  useEffect(() => {
    // 已知名字的非图片文件不预拉 —— 名字够展示，点开时再换新鲜预签名。
    if (fallbackKind !== 'image' && fallbackName) return
    let alive = true
    getUpload(uploadId)
      .then((i) => { if (alive) setInfo(i) })
      .catch(() => { if (alive) setFailed(true) })
    return () => { alive = false }
  }, [uploadId, fallbackKind, fallbackName])

  if (failed) {
    return (
      <div className="rounded-lg border border-line-subtle bg-panel px-3 py-2 text-xs text-faint">
        {t('artifact.expired')}
      </div>
    )
  }

  const kind = info?.kind || fallbackKind
  const name = info?.filename || fallbackName || 'file'

  if (kind === 'image') {
    if (!info) return <div className="h-32 w-44 animate-pulse rounded-lg bg-panel" />
    return (
      <a href={info.get_url} target="_blank" rel="noreferrer" className="block">
        <img
          src={info.get_url}
          alt={name}
          className="max-h-56 max-w-full rounded-lg border border-line-subtle object-cover"
        />
      </a>
    )
  }

  const openFile = async () => {
    setOpening(true)
    try {
      const fresh = await getUpload(uploadId)
      window.open(fresh.get_url, '_blank')
    } catch {
      setFailed(true)
    } finally {
      setOpening(false)
    }
  }

  const subParts: string[] = []
  if (fromDesktop) subParts.push(t('artifact.fromDesktop'))
  const sizeText = fmtSize(info?.size || size)
  if (sizeText) subParts.push(sizeText)

  return (
    <button
      onClick={() => void openFile()}
      className="flex w-full items-center gap-2.5 rounded-lg border border-line-subtle bg-panel px-3 py-2.5 text-left active:bg-elevated"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-elevated text-muted">
        <IconFile className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-primary">{name}</div>
        {subParts.length > 0 && <div className="text-[11px] text-dimmed">{subParts.join(' · ')}</div>}
      </div>
      {opening
        ? <Spinner className="h-4 w-4 shrink-0 text-accent" />
        : <IconDownload className="h-4 w-4 shrink-0 text-faint" />}
    </button>
  )
}
